import type { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import {
  CircleCheck,
  Download,
  Eye,
  Grid2X2,
  Loader,
  TagIcon,
  Trash2,
} from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { ClearFilters } from "@/components/clear-filters";
import { ResponsiveButton } from "@/components/responsive-button";
import { ServerDataTable } from "@/components/server-data-table";
import { CustomVerificationsList } from "@/components/tags/custom-verifications-list";
import { SkuDropdownMenu } from "@/components/tags/sku-dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useExportTagsMutation } from "@/data/mutations";
import { useGetTagsQuery } from "@/data/queries";
import type { Tag } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { NetworkNameByChainId } from "@/lib/chains";
import { canDeleteTag } from "@/lib/permissions";
import useStore from "@/state";

const columns: ColumnDef<Tag>[] = [
  {
    id: "uid",
    header: "Tag ID",
    accessorKey: "uid",
    cell: ({ row }) => {
      return (
        <Link to={`/tags/${row.original.uid}`}>
          <Button variant="outline" size="sm">
            <TagIcon /> {row.original.uid}
          </Button>
        </Link>
      );
    },
  },
  {
    id: "tags.tag_view_verifications_count",
    accessorKey: "tag_view_verifications_count",
    header: "Page Views",
    cell: ({ row }) => {
      return (
        <Link
          to={`/verifications?uid=${row.original.uid}&verifyType=user_view`}
        >
          <Button variant="outline" size="sm" className="w-[70px]">
            <Eye /> {row.original.tag_view_verifications_count}
          </Button>
        </Link>
      );
    },
  },
  {
    id: "template_id",
    accessorKey: "template_id",
    header: "SKU ID",
    cell: ({ row }) => {
      return (
        <Link to={`/skus/${row.original.template_id}`}>
          <Button variant="outline" size="sm" className="w-[96px]">
            <Grid2X2 /> {row.original.template_id}
          </Button>
        </Link>
      );
    },
  },
  {
    id: "name",
    accessorKey: "name",
    header: "SKU Name",
  },
  {
    header: "Tag Notes",
    accessorKey: "notes",
  },
  {
    id: "tags.created_at",
    accessorKey: "created_at",
    header: "Programmed at",
    cell: ({ row }) => {
      return (
        <div>
          {parseISO(row.original.created_at).toLocaleDateString()}
          <br />
          {parseISO(row.original.created_at).toLocaleTimeString()}
        </div>
      );
    },
  },
  {
    id: "nft_token_id",
    accessorKey: "nft_token_id",
    header: "Serial Number",
  },
  {
    header: "Quality Check",
    accessorKey: "qc",
    cell: ({ row }) => {
      return row.original.qc ? (
        <Badge variant="green">Passed</Badge>
      ) : (
        <Badge variant="orange">Pending</Badge>
      );
    },
  },
  {
    header: "Custom Verifications",
    accessorKey: "custom_verifications_count",
    cell: ({ row }) => {
      return (
        <Link to={`/verifications?verifyType=custom&uid=${row.original.uid}`}>
          <CustomVerificationsList tagUid={row.original.uid} />
        </Link>
      );
    },
  },
  {
    id: "counter",
    accessorKey: "counter",
    header: "Latest Read Sequence ID",
  },
  {
    header: "Latest Tamper Status",
    accessorKey: "tamper",
    cell: ({ row }) => {
      if (!row.original.tamper) {
        return <Badge variant="secondary">--</Badge>;
      } else if (row.original.tamper === "CC") {
        return <Badge variant="secondary">Sealed</Badge>;
      } else if (row.original.tamper === "OO") {
        return <Badge>Opened</Badge>;
      }
    },
    meta: {
      tooltip: "Status of tamper since its last verification",
    },
  },
  {
    header: "Tamper Detection",
    accessorKey: "tamper_enabled",
    cell: ({ row }) => {
      return row.original.tamper_enabled ? (
        <Badge>On</Badge>
      ) : (
        <Badge variant="secondary">Off</Badge>
      );
    },
  },
  {
    id: "tags.tag_verifications_count",
    accessorKey: "verifications",
    header: "Total Verifications",
    cell: ({ row }) => {
      return (
        <Link to={`/verifications?uid=${row.original.uid}`}>
          <Button variant="outline" size="sm" className="w-[70px]">
            <CircleCheck /> {row.original.verifications}
          </Button>
        </Link>
      );
    },
  },
  {
    id: "tags.claim_count",
    accessorKey: "claim_count",
    header: "Ownership Claims",
  },
  {
    header: "Blockchain",
    accessorKey: "nft_chain_id",
    cell: ({ row }) => {
      if (!row.original.nft_chain_id) {
        return "";
      } else {
        return (
          <Badge variant="secondary">
            {
              NetworkNameByChainId[
                row.original.nft_chain_id as keyof typeof NetworkNameByChainId
              ]
            }
          </Badge>
        );
      }
    },
  },
  {
    header: "Contract Address",
    accessorKey: "nft_contract_address",
  },
  {
    header: "Owner ID",
    accessorKey: "owner_address",
  },
  {
    header: "Marketing Consent",
    accessorKey: "email_marketing_consent",
    cell: ({ row }) => {
      return row.original.email_marketing_consent ? (
        <Badge variant="green">Yes</Badge>
      ) : (
        <Badge variant="secondary">No</Badge>
      );
    },
  },
  {
    header: "Delete",
    cell: ({ row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { openDialog } = useDialogState();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { session } = useStore();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const hasPermission = useMemo(
        () => canDeleteTag(session?.permissions),
        [session?.permissions],
      );

      if (!hasPermission) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className={"cursor-not-allowed"}>
                <Button variant="ghost" disabled>
                  <Trash2 className="stroke-destructive" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-popover shadow-md border">
                <p className="text-sm text-popover-foreground">
                  Contact Legitimate or your account admin to update your role
                  and permissions.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }

      return (
        <Button
          variant="ghost"
          onClick={() =>
            openDialog({
              name: "delete-tag",
              params: { tagId: row.original.uid },
            })
          }
        >
          <Trash2 className="stroke-destructive" />
        </Button>
      );
    },
  },
];

const TAGS_DEFAULT_SORT = {
  column: "tags.created_at",
  direction: "desc",
} as const;

export const Tags = () => {
  const { data, isError, error, isFetching } = useGetTagsQuery({
    defaultSort: TAGS_DEFAULT_SORT,
  });

  const exportMutation = useExportTagsMutation({
    defaultSort: TAGS_DEFAULT_SORT,
  });

  const handleExport = async () => {
    exportMutation.mutateAsync();
  };

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div>
      <div className="flex justify-between border-b pb-2">
        <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
          Tags
        </h2>
      </div>
      <ServerDataTable
        isLoading={isFetching}
        columns={columns}
        data={data?.tags}
        filterPlaceholder="Filter by Tag ID"
        filterInputColumn="uid"
        paginationInfo={data?.pagy}
        filterButtons={
          <div className="flex gap-2">
            <SkuDropdownMenu />
            <ClearFilters keys={["templateId"]} />
          </div>
        }
        extraButtons={
          <ResponsiveButton
            variant="secondary"
            disabled={exportMutation.isPending}
            onClick={handleExport}
            leftIcon={
              exportMutation.isPending ? (
                <Loader className="animate-spin" />
              ) : (
                <Download />
              )
            }
          >
            Download CSV
          </ResponsiveButton>
        }
        defaultColumnVisibility={{
          ["tags.claim_count"]: false,
          nft_chain_id: false,
          nft_contract_address: false,
          owner_address: false,
          email_marketing_consent: false,
          counter: false,
          notes: false,
          created_at: false,
          tamper: false,
          tamper_enabled: false,
          custom_verifications_count: false,
          qc: false,
        }}
        sortableColumns={[
          "uid",
          "tags.tag_view_verifications_count",
          "template_id",
          "name",
          "tags.created_at",
          "nft_token_id",
          "tags.latest_counter",
          "tags.tag_verifications_count",
          "tags.claim_count",
        ]}
        defaultSort={TAGS_DEFAULT_SORT}
      />
    </div>
  );
};
